import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import { Constants } from 'src/app/app.constants';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { ConfigurationDialogComponent } from 'src/app/shared/components/configuration-dialog/configuration-dialog.component';
import {
  IntegrationConfig,
  IntegrationConfiguration,
} from 'src/app/models/integrationConfig';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { ShopifyDataDialogComponent } from 'src/app/shared/components/shopify-data-dialog/shopify-data-dialog.component';
import { InviteDialogComponent } from 'src/app/shared/components/invite-dialog/invite-dialog.component';
import { AuthService, MerchantService } from 'src/app/services';
import { Integration } from '../integrations-home.component';

@Component({
  selector: 'integration-detail-dialog',
  templateUrl: 'integration-detail-dialog.component.html',
  styleUrls: ['./integration-detail-dialog.component.css'],
})
export class IntegrationDetailDialogComponent implements OnInit {
  integrations = {
    squarespace: 'squarespace',
    shopify: 'shopify',
    square: 'square',
    eposnow: 'eposnow',
    zapier: 'zapier',
    make: 'make',
  };

  error = false;
  saving = false;
  loading = false;
  syncing = false;

  tabs = ['overview'];
  selectedIndex = 0;
  selectedTab = 'overview';

  merchantActive = false;

  integration!: Integration;

  form!: FormGroup;

  zapierAPIKeyFormControl!: FormControl;
  makeAPIKeyFormControl!: FormControl;

  webhookFormControl!: FormControl;
  webhookEposNowFormControl!: FormControl;

  shopifySigningKey!: FormControl;

  eposNowAccessToken!: FormControl;

  addSquareCustomers = false;
  addEposNowCustomers = false;

  inviteUsersShopify = false;
  inviteUsersSquare = false;
  inviteUsersZapier = false;
  inviteUsersMake = false;
  inviteUsersEposNow = false;
  inviteUsersSquarespace = false;

  awardStampsMake = false;
  rewardAutomationMake = false;
  rewardTypeMake: 'product' | 'coupon' = 'coupon';
  selectedMakeItem?: string;
  makeCoupons: Array<{ id: string; name: string }> = [];
  makeProducts: Array<{ id: string; name: string }> = [];

  awardStampsZapier = false;
  rewardAutomationZapier = false;
  rewardTypeZapier: 'product' | 'coupon' = 'coupon';
  selectedZapierItem?: string;
  zapierCoupons: Array<{ id: string; name: string }> = [];
  zapierProducts: Array<{ id: string; name: string }> = [];

  zapierConfig?: IntegrationConfig;
  makeConfig?: IntegrationConfig;
  shopifyConfig?: IntegrationConfig;
  squareConfig?: IntegrationConfig;
  eposnowConfig?: IntegrationConfig;
  squarespaceConfig?: IntegrationConfig;

  hasShopify = false;
  hasEposNow = false;
  hasSquare = false;
  hasSquarespace = false;
  hasZapier = false;
  hasMake = false;

  hasIntegration = false;
  canSave = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<IntegrationDetailDialogComponent>,
    public configDialogRef: MatDialogRef<ConfigurationDialogComponent>,
    public fb: FormBuilder,
    public dialog: MatDialog,
    private merchantService: MerchantService,
    private authService: AuthService,
    public constants: Constants,
    private formBuilder: FormBuilder,
  ) {
    this.integration = this.data.integration as Integration;

    this.hasShopify =
      this.authService.getNestedUserProperty('merchant', 'shopify') ?? false;

    this.hasSquare =
      this.authService.getNestedUserProperty('merchant', 'square') ?? false;

    this.hasEposNow =
      this.authService.getNestedUserProperty('merchant', 'eposnow') ?? false;

    this.hasZapier =
      this.authService.getNestedUserProperty('merchant', 'zapier') ?? false;

    this.hasSquarespace =
      this.authService.getNestedUserProperty('merchant', 'squarespace') ??
      false;

    this.hasMake =
      this.authService.getNestedUserProperty('merchant', 'make') ?? false;

    if (this.integration.value === this.integrations.shopify) {
      this.hasIntegration = this.hasShopify;
    } else if (this.integration.value === this.integrations.square) {
      this.hasIntegration = this.hasSquare;
    } else if (this.integration.value === this.integrations.eposnow) {
      this.hasIntegration = this.hasEposNow;
    } else if (this.integration.value === this.integrations.zapier) {
      this.hasIntegration = this.hasZapier;
    } else if (this.integration.value === this.integrations.squarespace) {
      this.hasIntegration = this.hasSquarespace;
    } else if (this.integration.value === this.integrations.make) {
      this.hasIntegration = this.hasMake;
    } else {
      this.hasIntegration = false;
    }

    this.zapierAPIKeyFormControl = new FormControl({
      value: '',
      disabled: true,
    });

    this.makeAPIKeyFormControl = new FormControl({
      value: '',
      disabled: true,
    });

    const nanoid = this.authService.getNestedUserProperty('merchant', 'nanoid');
    const webhookUrl = `https://api.remyrewards.co.uk/v1/webhooks/orders/create/${nanoid}`;
    const webhookUrlEposNow = `/v1/webhooks/orders/create/${nanoid}`;

    this.webhookFormControl = new FormControl({
      value: webhookUrl,
      disabled: true,
    });

    this.webhookEposNowFormControl = new FormControl({
      value: webhookUrlEposNow,
      disabled: true,
    });

    this.shopifySigningKey = new FormControl({ value: '', disabled: false });

    this.eposNowAccessToken = new FormControl({ value: '', disabled: false });

    this.form = this.formBuilder.group({
      zapierApiKey: this.zapierAPIKeyFormControl,
      makeApiKey: this.makeAPIKeyFormControl,
      webhookUrl: this.webhookFormControl,
      webhookEposNowUrl: this.webhookEposNowFormControl,
      shopifySigningKey: this.shopifySigningKey,
      eposNowAuthToken: this.eposNowAccessToken,
    });
  }

  ngOnInit(): void {
    this.merchantActive = this.authService.merchantObj?.active ?? true;

    this.loadData();
  }

  copyApiKey(formControl: FormControl, integration: string): void {
    const copyHandler = (e: ClipboardEvent) => {
      e.clipboardData!.setData('text/plain', formControl.value);
      e.preventDefault();
      document.removeEventListener('copy', copyHandler);
    };

    document.addEventListener('copy', copyHandler);

    navigator.clipboard
      .writeText(formControl.value)
      .then(() => {
        this.constants.snack(`${integration} API key copied to clipboard`);
      })
      .catch((err) => {
        console.error('Failed to copy text to clipboard:', err);
      });
  }

  copyWebhookUrl(): void {
    const copyHandler = (e: ClipboardEvent) => {
      e.clipboardData!.setData('text/plain', this.webhookFormControl.value);
      e.preventDefault();
      document.removeEventListener('copy', copyHandler);
    };

    document.addEventListener('copy', copyHandler);

    navigator.clipboard
      .writeText(this.webhookFormControl.value)
      .then(() => {
        this.constants.snack('Webhook URL copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy text to clipboard:', err);
      });
  }

  loadData(): void {
    const clearValidatorsFor = (controls: AbstractControl[]) => {
      controls.forEach((control) => control.clearValidators());
    };

    const setValidatorsFor = (
      controls: { control: AbstractControl; validators: ValidatorFn[] }[],
    ) => {
      controls.forEach(({ control, validators }) =>
        control.setValidators(validators),
      );
    };

    const updateValidityFor = (controls: AbstractControl[]) => {
      controls.forEach((control) => control.updateValueAndValidity());
    };

    const allControls = [
      this.shopifySigningKey,
      this.eposNowAccessToken,
      this.zapierAPIKeyFormControl,
      this.makeAPIKeyFormControl,
    ];

    const integrationsConfig = {
      [this.integrations.shopify]: {
        validators: [
          {
            control: this.shopifySigningKey,
            validators: [Validators.required],
          },
        ],
        action: () => this.getShopifyIntegration(),
      },
      [this.integrations.eposnow]: {
        validators: [
          {
            control: this.eposNowAccessToken,
            validators: [Validators.required],
          },
        ],
        action: () => this.getEposNowIntegration(),
      },
      [this.integrations.squarespace]: {
        validators: [],
        action: () => this.getSquarespaceIntegration(),
      },
      [this.integrations.square]: {
        validators: [],
        action: () => this.getSquareIntegration(),
      },
      [this.integrations.make]: {
        validators: [
          {
            control: this.makeAPIKeyFormControl,
            validators: [Validators.required],
          },
        ],
        action: () => this.getMakeIntegration(),
      },
      default: {
        validators: [
          {
            control: this.zapierAPIKeyFormControl,
            validators: [Validators.required],
          },
        ],
        action: () => this.getZapierIntegration(),
      },
    };

    const selectedConfig =
      integrationsConfig[
        this.integration.value as keyof typeof integrationsConfig
      ] || integrationsConfig.default;

    selectedConfig.action();
    setValidatorsFor(selectedConfig.validators);
    updateValidityFor(allControls);
  }

  showInfoDialog(title: string, body: string): void {
    this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: title,
        body: body,
        buttonTwo: 'Close',
      },
      width: '450px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  viewExampleMail(): void {
    window.open(
      'https://remy-system.s3.eu-west-2.amazonaws.com/Invite+email+example.png',
      '_blank',
    );
  }

  get overviewText(): string {
    if (this.integration.value === this.integrations.zapier) {
      return `Use our <b>${this.integration.label}</b> integration to automatically give
        stamps and points to your customers for purchases from <b>7000+ apps</b> including online ordering platforms
        (e.g. SquareSpace, Square, Shopify, WooCommerce), booking systems (e.g. acuity, fresha, treatwell), and many more.<br><br>

        <b>What you can do:</b><br>
        • Automatically award stamps/points based on order value<br>
        • Set up multiple configurations for different order sources<br>
        • Automatically redeem coupon codes from Remy rewards<br>
        • Invite new customers via email if they don't have a Remy account yet<br>
        • Track orders from any platform that integrates with Zapier<br><br>

        <b>Requirements:</b> Customers must be pre-registered on Remy,
        have added your loyalty card(s) to their Remy wallet, and
        place orders using the email associated with their Remy
        account in order for the linking to work. <br><a href="${this.integration.link}" target="_blank">${this.integration.link}</a>
      `;
    }

    if (this.integration.value === this.integrations.make) {
      return `Use our <b>${this.integration.label}</b> integration to automatically give
        stamps and points to your customers for purchases from <b>3000+ apps</b> including online ordering platforms
        (e.g. SquareSpace, Square, Shopify, WooCommerce), booking systems (e.g. acuity, fresha, treatwell), and many more.<br><br>

        <b>What you can do:</b><br>
        • Automatically award stamps/points based on order value<br>
        • Set up multiple configurations for different order sources<br>
        • Automatically redeem coupon codes from Remy rewards<br>
        • Invite new customers via email if they don't have a Remy account yet<br>
        • Track orders from any platform that integrates with Make<br><br>

        <b>Requirements:</b> Customers must be pre-registered on Remy,
        have added your loyalty card(s) to their Remy wallet, and
        place orders using the email associated with their Remy
        account in order for the linking to work. <br><a href="${this.integration.link}" target="_blank">${this.integration.link}</a>
      `;
    }

    return `Use our <b>${this.integration.label}</b> integration to automatically give
        stamps to your customers for purchases in your online
        store or in-store EPos. <b>Customers must be pre-registered</b> on Remy,
        have added your loyalty card(s) to their Remy wallet, and
        place orders using the email associated with their Remy
        account in order for the linking to work. <br><a href="${this.integration.link}">${this.integration.link}</a>
      `;
  }

  get titleOne(): string {
    return 'Shopify Integration Setup';
  }

  get titleOneMake(): string {
    return 'Make Integration Setup';
  }

  get titleOneZapier(): string {
    return 'Zapier Integration Setup';
  }

  get titleOneSquare(): string {
    return 'Square Integration Setup';
  }

  get titleOneEposnow(): string {
    return 'EposNow Integration Setup';
  }

  get titleTwo(): string {
    return 'Webhook Signing Key';
  }

  get titleTwoSquare(): string {
    return 'Webhook Signature Key';
  }

  get bodyOne(): string {
    return `You'll need to create a new Webhook in your Shopify store using this URL, following these instructions:<br><br>

    1. Navigate to your <a href="https://admin.shopify.com/store" target="_blank">Shopify store</a><br>
    2. Open <b>Settings</b><br>
    3. Click on <b>Notifications</b> in the left sidebar<br>
    4. Open <b>Webhooks</b><br>
    5. Create a new Webhook<br>
    6. Select the <b>Order Payment</b> Event, and the <b>JSON</b> format<br>
    7. Paste your unique <b>Webhook URL</b> into the URL field<br>
    8. Select the <b>latest</b> Webhook API version<br>
    9. Your Webhook is now set up, and Remy will be notified when orders have been paid for
  `;
  }

  get bodyOneMake(): string {
    return `Follow the below instructions to get started with the Make x Remy integration:<br><br>

    1. Navigate to <a href="https://www.make.com/" target="_blank">Make</a><br>
    2. Create a new account or log in to your existing account<br>
    3. Follow this link to add the Remy integration <a href="https://eu1.make.com/app/invite/eda18640740df7d85b49553ea02ad21f" target="_blank">HERE</a><br>
    2. Create a new scenario<br>
    3. Add the <b>Remy Rewards</b> integration<br>
    4. Securely connect your scenario to Remy using your API key <br>
    5. Select the <b>Create Order</b> event<br>
    6. Ensure that the Customer Email, Order Number, Order Value and Order Source are populated from your chosen source (e.g. storekit, deliveroo or just eat order details)<br>
    7. Your Make integration is now set up, and Remy will be notified when orders have been placed from your chosen source<br>
    8. It's important to note that the customer must already have your Remy loyalty card using the same email address provided in the Make scenario<br>
    9. Refer to this <a href="https://remy-rewards.notion.site/Remy-x-Make-Integration-Guide-35004100010041008000000000000000" target="_blank">Make x Remy integration guide</a> for more information
  `;
  }

  get bodyOneZapier(): string {
    return `Follow the below instructions to get started with the Zapier x Remy integration:<br><br>

    1. Navigate to <a href="https://zapier.com/apps" target="_blank">Zapier</a><br>
    2. Add the <b>Remy Rewards</b> integration<br>
    3. Securely connect your Zap to Remy using your API key <br>
    4. Select the <b>Create Order</b> event<br>
    5. Ensure that the Customer Email, Order Number, Order Value and Order Source are populated from your chosen source (e.g. storekit, deliveroo or just eat order details)<br>
    6. Your Zapier integration is now set up, and Remy will be notified when orders have been placed from your chosen source<br>
    7. It's important to note that the customer must already have your Remy loyalty card using the same email address provided in the Zap<br>
    8. Refer to this <a href="https://remy-rewards.notion.site/Remy-x-Zapier-Integration-Guide-4daaafed390b4a6aa1595b92ab24ff02" target="_blank">Zapier x Remy integration guide</a> for more information
  `;
  }

  get bodyOneSquare(): string {
    return `You'll need to create a new App and Webhook in your <b>Square</b> store using this URL, following these instructions:<br><br>

    1. Navigate to your <a href="https://developer.squareup.com/apps" target="_blanl">Square dashboard</a><br>
    2. Create a new App with the name <b>Remy</b> and skip the next two steps<br>
    3. Open the newly created App, and open <b>Webhooks/Subscriptions</b> from the left sidebar<br>
    4. Ensure that the <b>Production</b> tab is selected (not Sandbox)<br>
    5. Select <b>Add subscription</b><br>
    6. Give your webhook the name <b>remy-webook</b><br>
    7. Paste your <b>Webhook URL</b> into the <b>URL</b> field<br>
    8. Select the <b>latest</b> API version<br>
    9. Select the <b>payment.created</b> Event only<br>
    10. Save your webhook<br>
    11. Click on the new webhook you just created, and copy the <b>Signature key</b>, you'll need this for the next step
  `;
  }

  get bodyOneEposNow(): string {
    return `You'll need to create a new Webhook in your <b>EposNow</b> Back Office using this URL, following these instructions:<br><br>

    1. Navigate to your <a href="https://www.eposnowhq.com/Pages/BackOffice/Integrations/WebHooks/WebHookList.aspx" target="_blank">Epos Now back-office</a><br>
    2. Click on <b>ADD WEBHOOK TRIGGGER</b><br>
    3. Select the API device you created earlier, the one you called <b>Remy</b><br>
    4. Enter the Webhook Route Path into the <b>Route Path</b> field<br>
    5. Select the <b>Complete Transaction</b> event<br>
    6. Click <b>ADD</b> to save the webhook
  `;
  }

  get bodyTwo(): string {
    return `Locate your Shopify Webhook Signing Key in the Webhook section, as outlined in the previous step. It's identified by a lengthy character string next to the label <b>'Your webhooks will be signed with'</b> and paste it here`;
  }

  get bodyTwoSquare(): string {
    return `Click on your newly created Square Webhook, and copy the <b>Signature key</b> and paste it here`;
  }

  get accessTokenTitle(): string {
    return `Square Access Token`;
  }

  get accessTokenTitleEposnow(): string {
    return `Epos Now Authorization Token`;
  }

  get accessTokenBody(): string {
    return `Open the newly created app in your <a href="https://developer.squareup.com/apps" target="_blank">Square dashboard</a>, then follow these instructions:<br><br>

    1. Click on the <b>Credentials</b> tab in the left sidebar<br>
    2. Ensure that the <b>Production</b> segment is selected<br>
    3. Copy the <b>Production Access Token</b><br><br>
    <i><b>Important</b>: When copying the Access Token, make sure to copy the un-masked version of this, and not just the bullet points you see (••••)</i>
  `;
  }

  get accessTokenBodyEposnow(): string {
    return `
    Open the API app in your EposNow back office interface
    <a href="https://www.eposnowhq.com/Pages/BackOffice/Integrations/REST/RESTManagement.aspx" target="_blank">HERE</a>, then follow these instructions:<br><br>

    1. Click on the <b>REST API</b> tab in the left sidebar<br>
    2. Create a new API Device, you can call this <b>Remy</b> and set the location to your main store<br>
    3. Paste the following URL into the <b>API URL</b> field: <b>https://api.remyrewards.co.uk</b><br>
    4. Click on the <b>Access Credentials</b> button<br>
    5. Copy the <b>Authorization Token</b> and paste it into this field
  `;
  }

  remove(): void {
    const dialog = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: `Remove ${this.integration.label} Integration`,
        body: `Removing your ${
          this.integration.label
        } integration will mean that purchases ${
          this.integration.value === 'zapier'
            ? 'captured through your Zapier integration'
            : this.integration.value === 'make'
              ? 'captured through your Make integration'
              : 'made in your ' + this.integration.label + ' store'
        } will no longer by linked to your Remy customer loyalty cards<br><br>Do you want to continue with the de-integration?`,
        buttonOne: 'Cancel',
        buttonTwo: 'Continue',
      },
      width: '500px',
      autoFocus: false,
      disableClose: true,
      scrollStrategy: new NoopScrollStrategy(),
      panelClass: 'custom-dialog',
    });

    dialog.afterClosed().subscribe((option: boolean) => {
      if (option) {
        this.removeIntegration();
      }
    });
  }

  getShopifyIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration(this.integrations.shopify)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.shopifyConfig = res;

            this.shopifyConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.shopifySigningKey.setValue(this.shopifyConfig?.signingKey);

            this.inviteUsersShopify =
              this.shopifyConfig.inviteUser?.active ?? false;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  getSquareIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration(this.integrations.square)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.squareConfig = res;
            this.squareConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.addSquareCustomers = this.squareConfig.addCustomers ?? false;

            this.inviteUsersSquare =
              this.squareConfig.inviteUser?.active ?? false;

            if (!this.squareConfig?.oauth?.updatedOn) {
              this.canSave = false;
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  getEposNowIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration(this.integrations.eposnow)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.eposnowConfig = res;
            this.eposnowConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.eposNowAccessToken.setValue(this.eposnowConfig?.accessToken);

            this.addEposNowCustomers = this.eposnowConfig.addCustomers ?? false;

            this.inviteUsersEposNow =
              this.eposnowConfig.inviteUser?.active ?? false;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  onRewardTypeMakeChange(): void {
    this.selectedMakeItem = undefined;
  }

  getMakeIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration(this.integrations.make)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.makeConfig = res;
            this.makeConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.makeAPIKeyFormControl.setValue(this.makeConfig.signingKey);

            this.inviteUsersMake = this.makeConfig.inviteUser?.active ?? false;
            this.awardStampsMake =
              this.makeConfig.makeSettings?.awardStamps ?? false;
            this.rewardAutomationMake =
              this.makeConfig.makeSettings?.rewardAutomation?.enabled ?? false;
            this.rewardTypeMake =
              this.makeConfig.makeSettings?.rewardAutomation?.type ?? 'coupon';
            this.selectedMakeItem =
              this.rewardTypeMake === 'coupon'
                ? this.makeConfig.makeSettings?.rewardAutomation?.couponId
                : this.makeConfig.makeSettings?.rewardAutomation?.productId;
          }
          // If no config exists, the "Generate API Key" button will be shown in the UI
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  getZapierIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration(this.integrations.zapier)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.zapierConfig = res;
            this.zapierConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.zapierAPIKeyFormControl.setValue(this.zapierConfig.signingKey);

            this.inviteUsersZapier =
              this.zapierConfig.inviteUser?.active ?? false;
            this.awardStampsZapier =
              this.zapierConfig.zapierSettings?.awardStamps ?? false;
            this.rewardAutomationZapier =
              this.zapierConfig.zapierSettings?.rewardAutomation?.enabled ??
              false;
            this.rewardTypeZapier =
              this.zapierConfig.zapierSettings?.rewardAutomation?.type ??
              'coupon';
            this.selectedZapierItem =
              this.rewardTypeZapier === 'coupon'
                ? this.zapierConfig.zapierSettings?.rewardAutomation?.couponId
                : this.zapierConfig.zapierSettings?.rewardAutomation?.productId;
          }
          // If no config exists, the "Generate API Key" button will be shown in the UI
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  generateMakeApiKey(): void {
    this.loading = true;
    this.merchantService
      .initializeMake()
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.makeConfig = res;
            this.makeAPIKeyFormControl.setValue(res.signingKey);
            this.constants.snack('API Key generated successfully');
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  generateZapierApiKey(): void {
    this.loading = true;
    this.merchantService
      .initializeZapier()
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.zapierConfig = res;
            this.zapierAPIKeyFormControl.setValue(res.signingKey);
            this.constants.snack('API Key generated successfully');
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  getActiveSubscriptions(config?: IntegrationConfig) {
    return config?.subscriptions?.filter((sub) => sub.active) || [];
  }

  formatSubscriptionEvent(event?: string): string {
    if (!event) return '';
    // Convert event names like 'customer.created' to 'Customer Created'
    return event
      .split('.')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getSquarespaceIntegration(): void {
    this.loading = true;

    this.merchantService
      .getIntegration('squarespace')
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.squarespaceConfig = res;
            this.squarespaceConfig.configurations?.sort((a, b) =>
              (a?.type ?? 'order').localeCompare(b?.type ?? 'order'),
            );

            this.inviteUsersSquarespace =
              this.squarespaceConfig.inviteUser?.active ?? false;
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  save(): void {
    if (this.form.invalid) {
      this.constants.snack('Please complete all required fields');

      // Loop through form controls to find invalid ones
      Object.keys(this.form.controls).forEach((key) => {
        const controlErrors = this.form.get(key)?.errors;
        if (controlErrors) {
          console.log(`Field: ${key}, Errors:`, controlErrors);
        }
      });

      return;
    }

    this.saving = true;

    if (this.integration.value === 'shopify') {
      this.saveShopify();
    } else if (this.integration.value === 'square') {
      this.saveSquare();
    } else if (this.integration.value === 'eposnow') {
      this.saveEposNow();
    } else if (this.integration.value === 'squarespace') {
      this.saveSquarespace();
    } else if (this.integration.value === 'make') {
      this.saveMake();
    } else {
      this.saveZapier();
    }
  }

  saveShopify(): void {
    const key = this.shopifySigningKey.value?.trim();

    this.merchantService
      .addShopify({
        key: key,
        inviteUsers: this.inviteUsersShopify,
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Shopify Integration Updated');

            if (this.shopifyConfig) {
              this.shopifyConfig.signingKey = key;
            }

            if (!this.shopifyConfig?.configurations?.length) {
              this.addConfigDialog();
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  saveEposNow(): void {
    const token = this.eposNowAccessToken?.value?.trim();

    this.merchantService
      .addEposNow({
        token: token,
        addCustomers: this.addEposNowCustomers,
        inviteUsers: this.inviteUsersEposNow,
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('EposNow Integration Updated');

            if (this.eposnowConfig) {
              this.eposnowConfig.accessToken = token;
            }

            if (!this.eposnowConfig?.configurations?.length) {
              this.addConfigDialog();
            }

            this.matDialogRef.close();
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  saveSquare(): void {
    this.merchantService
      .addSquare({
        addCustomers: this.addSquareCustomers,
        inviteUsers: this.inviteUsersSquare,
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Square Integration Updated');

            if (!this.squareConfig?.configurations?.length) {
              this.addConfigDialog();
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  saveMake(): void {
    const key = this.makeAPIKeyFormControl?.value?.trim();
    const isFirstTimeSetup = !this.makeConfig?.signingKey;

    this.merchantService
      .addMake({
        key: key,
        inviteUsers: this.inviteUsersMake,
        awardStamps: this.awardStampsMake,
        rewardAutomation: {
          enabled: this.rewardAutomationMake,
          type: this.rewardTypeMake,
          couponId:
            this.rewardTypeMake === 'coupon'
              ? this.selectedMakeItem
              : undefined,
          productId:
            this.rewardTypeMake === 'product'
              ? this.selectedMakeItem
              : undefined,
        },
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            if (this.makeConfig) {
              this.makeConfig.signingKey = key;
            }

            // Only show dialog and auto-switch on first-time setup
            if (isFirstTimeSetup) {
              this.addMakeConfigDialog();
              this.selectedIndex = 2;
            } else {
              this.constants.snack('Make Integration Updated');
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  saveZapier(): void {
    const isFirstTimeSetup = !this.zapierConfig?.zapierSettings;

    this.merchantService
      .addZapier({
        inviteUsers: this.inviteUsersZapier,
        awardStamps: this.awardStampsZapier,
        rewardAutomation: {
          enabled: this.rewardAutomationZapier,
          type: this.rewardTypeZapier,
          couponId:
            this.rewardTypeZapier === 'coupon' ? this.selectedZapierItem : null,
          productId:
            this.rewardTypeZapier === 'product'
              ? this.selectedZapierItem
              : null,
        },
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            // Update config with response
            this.zapierConfig = res;
            if (this.zapierConfig?.signingKey) {
              this.zapierAPIKeyFormControl.setValue(
                this.zapierConfig.signingKey,
              );
              this.zapierAPIKeyFormControl.enable();
            }

            // Only show dialog and auto-switch on first-time setup
            if (isFirstTimeSetup) {
              this.addZapierConfigDialog();
              this.selectedIndex = 2;
            } else {
              this.constants.snack('Zapier Integration Updated');
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  saveSquarespace(): void {
    this.merchantService
      .addSquarespace({
        inviteUsers: this.inviteUsersSquarespace,
      })
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack('Squarespace Integration Updated');
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.saving = false));
  }

  addConfigDialog(): void {
    this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Configurations',
        body: 'You need to add at least one configuration before your integration is complete, you can do this by clicking on "<b>+ Add a Configuration"</b>',

        buttonTwo: 'OK',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }

  addMakeConfigDialog(): void {
    this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Connection Established',
        body: 'Your Make connection has been established. You can now configure your integration settings in the <b>Configuration</b> tab.',

        buttonTwo: 'OK',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }

  addZapierConfigDialog(): void {
    this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Connection Established',
        body: 'Your Zapier connection has been established. You can now configure your integration settings in the <b>Configuration</b> tab.',

        buttonTwo: 'OK',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      width: '350px',
      disableClose: true,
      panelClass: 'custom-dialog',
    });
  }

  removeIntegration(): void {
    const type = this.integration.value;

    this.saving = true;

    if (type === this.integrations.squarespace) {
      this.merchantService
        .disconnectSquarespace()
        .subscribe({
          next: (res: any) => {
            if (res) {
              this.constants.snack(
                `${this.integration.label} integration removed`,
              );

              this.constants
                .callbackSnack(
                  'Squarespace integration removed successfully',
                  true,
                )
                .subscribe(() => {
                  this.matDialogRef.close();
                  location.reload();
                });
            }
          },
          error: (res: HttpErrorResponse) => {
            this.constants.snack(res.error.message);
          },
        })
        .add(() => (this.saving = false));
    } else {
      this.merchantService
        .removeIntegration(type)
        .subscribe({
          next: (res: any) => {
            if (res) {
              this.constants
                .callbackSnack(
                  `${this.integration.label} integration removed successfully`,
                  true,
                )
                .subscribe(() => {
                  this.matDialogRef.close();
                  location.reload();
                });
            }
          },
          error: (res: HttpErrorResponse) => {
            this.constants.snack(res.error.message);
          },
        })
        .add(() => (this.saving = false));
    }
  }

  get canAddConfiguration(): boolean {
    // if (this.config?.configurations?.length) {
    //   return false;
    // }

    return true;
  }

  addConfiguration(): void {
    var canAddOrderValue = false;

    if (!this.config?.configurations?.find((el) => el.type === 'order')) {
      canAddOrderValue = true;
    }

    this.configDialogRef = this.dialog.open(ConfigurationDialogComponent, {
      width: '500px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      data: {
        orderAvailable: canAddOrderValue,
        type: this.integration.label,
      },
      scrollStrategy: new NoopScrollStrategy(),
    });

    this.configDialogRef.afterClosed().subscribe((data: any) => {
      if (data) {
        this.addNewConfiguration(data);
      }
    });
  }

  addNewConfiguration(data: IntegrationConfiguration): void {
    this.loading = true;

    this.merchantService
      .addIntegrationConfiguration(data, this.integration.value)
      .subscribe({
        next: (res?: IntegrationConfig) => {
          if (res) {
            this.configDialogRef.close();
            this.updateConfigurations(res);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  updateConfigurations(res: IntegrationConfig): void {
    let config: IntegrationConfig | undefined;

    switch (this.integration.value) {
      case this.integrations.eposnow:
        config = this.eposnowConfig;
        break;
      case this.integrations.shopify:
        config = this.shopifyConfig;
        break;
      case this.integrations.square:
        config = this.squareConfig;
        break;
      case this.integrations.squarespace:
        config = this.squarespaceConfig;
        break;
      case this.integrations.make:
        config = this.makeConfig;
        break;
      default:
        config = this.zapierConfig;
    }

    if (res.configurations) {
      if (config) {
        config.configurations = res.configurations;
      }
    }
  }

  get config(): IntegrationConfig | undefined {
    let config: IntegrationConfig | undefined;

    switch (this.integration.value) {
      case this.integrations.eposnow:
        config = this.eposnowConfig;
        break;
      case this.integrations.shopify:
        config = this.shopifyConfig;
        break;
      case this.integrations.square:
        config = this.squareConfig;
        break;
      case this.integrations.squarespace:
        config = this.squarespaceConfig;
        break;
      case this.integrations.make:
        config = this.makeConfig;
        break;
      default:
        config = this.zapierConfig;
    }

    return config;
  }

  editConfiguration(data: IntegrationConfiguration): void {
    this.configDialogRef = this.dialog.open(ConfigurationDialogComponent, {
      width: '500px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
      data: {
        configuration: data,
        type: this.integration.label,
      },
    });

    this.configDialogRef.afterClosed().subscribe((data: any) => {
      if (data) {
        this.updateConfiguration(data);
      }
    });
  }

  updateConfiguration(data: IntegrationConfiguration): void {
    this.loading = true;

    this.merchantService
      .updateIntegrationConfiguration(data, this.integration.value)
      .subscribe({
        next: (res: IntegrationConfig) => {
          if (res) {
            this.configDialogRef.close();
            this.updateConfigurations(res);
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  deleteConfiguration(id: string): void {
    const dialog = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Remove Configuration',
        body: 'Do you want to remove this configuration?',
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      width: '400px',
      autoFocus: false,
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialog.afterClosed().subscribe((option: boolean) => {
      if (option) {
        this.loading = true;

        this.merchantService
          .deleteIntegrationConfiguration(id, this.integration.value)
          .subscribe({
            next: (res?: IntegrationConfig) => {
              if (res) {
                this.updateConfigurations(res);
              }
            },
            error: (res: HttpErrorResponse) => {
              this.constants.snack(res.error.message);
            },
          })
          .add(() => (this.loading = false));
      }
    });
  }

  viewData(): void {
    this.dialog.open(ShopifyDataDialogComponent, {
      data: {
        title:
          this.integration.value === 'shopify'
            ? 'Shopify Orders (Qualifying)'
            : this.integration.value == 'square'
              ? 'Square Orders (Qualifying)'
              : this.integration.value === 'eposnow'
                ? 'EposNow Orders (Qualifying)'
                : this.integration.value === 'squarespace'
                  ? 'Squarespace Orders (Qualifying)'
                  : 'Zapier Orders (Qualifying)',
        type: this.integration.value,
      },
      autoFocus: false,
      width: '1200px',
      minHeight: '600px',
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  viewInvites(): void {
    this.dialog.open(InviteDialogComponent, {
      data: {
        title: 'Invites sent to customers',
        type: this.integration.value,
      },
      autoFocus: false,
      width: '1200px',
      minHeight: '600px',
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });
  }

  get infoIconTooltip(): string {
    return 'Customers may qualify for multiple stamps through various value and product configurations. Use this setting to limit customers to a single stamp per order, regardless of the number of qualifying products';
  }

  syncCustomers(integrationType: string): void {
    this.syncing = true;

    this.merchantService
      .syncCustomersFromIntegration(integrationType)
      .subscribe({
        next: (res: any) => {
          if (res) {
            const message =
              res.message ||
              `Successfully synced customers from ${integrationType}`;
            this.constants.snack(message);

            if (res.errors && res.errors.length > 0) {
              console.warn('Sync errors:', res.errors);
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message || 'Failed to sync customers');
        },
      })
      .add(() => (this.syncing = false));
  }

  get addCustomersTooltip(): string {
    return 'Enable this for customers to be automatically added to your Epos customer list when they take one of your Remy loyalty cards for the first time, their email and name will be taken';
  }

  get squareScanCustomersNote(): string {
    return `<b>Tip:</b> With this enabled, your customers' Remy loyalty card QR code can be scanned at checkout to automatically attach them to the transaction in Square. Enable <b>Settings &rsaquo; Checkout &rsaquo; Customer Management &rsaquo; Scan customers using device camera</b> on your Square Point of Sale, then tap the customer scan icon during checkout and scan the QR on their Remy loyalty card. <a href="https://my.squareup.com/help/us/en/article/7387-identify-customers-at-your-counter-with-qr-codes" target="_blank">Learn more</a>.`;
  }

  get inviteUsersTooltip(): string {
    return 'Enable this feature to immediately email customers without a Remy account or loyalty card, inviting them to join - they will only be emailed once';
  }

  get awardStampsMakeTooltip(): string {
    return 'When enabled, customers will automatically receive stamps or points when orders are created through Make based on your configurations. Disable this if you only want to use the integration for reward redemption.';
  }

  get rewardAutomationMakeTooltip(): string {
    return 'When enabled, orders created through Make will automatically check for coupon codes and mark rewards as redeemed when a valid coupon code is used.';
  }

  get awardStampsZapierTooltip(): string {
    return 'When enabled, customers will automatically receive stamps or points when orders are created through Zapier based on your configurations. Disable this if you only want to use the integration for reward redemption.';
  }

  get rewardAutomationZapierTooltip(): string {
    return 'When enabled, orders created through Zapier will automatically check for coupon codes and mark rewards as redeemed when a valid coupon code is used.';
  }

  rowLabel(item: IntegrationConfiguration) {
    let stampString = item.numberOfStamps === 1 ? 'stamp' : 'stamps';
    let pointString = item.numberOfPoints === 1 ? 'point' : 'points';
    let str = item.numberOfStamps ? stampString : pointString;
    let qty = item.numberOfStamps ?? item.numberOfPoints;

    if (item.type === 'order') {
      if (item?.method === 'fixed' || item.numberOfStamps) {
        return `Spend ${item?.value} or more to get ${qty} ${str}`;
      } else {
        const roundingLabel = this.pointRoundingLabel(item.pointsRoundingMode);
        const extras = [
          roundingLabel,
          item?.cap ? 'up to ' + item?.cap + ' points' : null,
        ].filter(Boolean);

        return `Earn ${qty} ${str} for every ${item?.value} spent ${
          extras.length ? '(' + extras.join(', ') + ')' : ''
        }`;
      }
    } else {
      return `${'SKU ' + item?.sku} (${qty} ${str})`;
    }
  }

  pointRoundingLabel(mode?: string): string {
    if (mode === 'up') {
      return 'round up';
    }

    if (mode === 'nearest') {
      return 'round to nearest';
    }

    if (mode === 'exact') {
      return 'exact points';
    }

    return 'round down';
  }

  connectOauth(): void {
    this.loading = true;

    this.merchantService.generateOauthUrl(this.integration.value).subscribe({
      next: (res: string) => {
        if (res) {
          location.href = res;
        }
      },
      error: (res: HttpErrorResponse) => {
        this.loading = false;
        this.constants.snack(res.error.message);
      },
    });
    // .add(() => (this.loading = false));
  }

  tabChanged(index: any): void {
    let tab = this.tabs[index];

    this.selectedTab = tab;
  }

  isNotEmpty = (obj: any): boolean => {
    if (!obj) {
      return false;
    }

    return Object.keys(obj).length > 0;
  };
}
